import type { LaneDef, StatusDef } from '../../api/types';

/**
 * 🔴 THE CONSTRAINT THIS FILE EXISTS FOR: THERE ARE 26 STATUSES, AND A 26-COLUMN BOARD IS NOT A BOARD.
 *
 * `status_def.lane` is on every status precisely so a board can default to SIX columns — one per lane
 * (`intake · dev · integrate · review · prod · terminal`) — and expand a lane to the statuses inside
 * it when someone actually needs that detail. The alternative, a horizontally-scrolling strip of 26
 * columns, is technically a board and practically a spreadsheet nobody can read.
 *
 * ⛔ TWO THINGS THIS MUST NOT DO, both of which end with a card that is simply not on the board:
 *   1. drop a status that is INACTIVE. `is_active=false` means "not offered as somewhere new to go"
 *      (db/004), NOT "no ticket is in it" — a retired status keeps whatever was already there.
 *   2. drop a status the vocabulary does not know at all. That happens the moment an admin adds one
 *      in Settings and this client's cached vocabulary is a minute old, and `WAIT API` on Or's real
 *      board is exactly such a status.
 *   ⇒ Both land in the `unplaced` column, visibly, rather than vanishing. ⭐ A card that disappears
 *     from a board reads as "someone deleted my ticket", and no error is shown anywhere.
 */

export type BoardColumnKind = 'lane' | 'status' | 'unplaced';

export interface BoardColumn {
  /** Stable key — React's key, the drop-target id, and the URL's expanded-lane token all use it. */
  key: string;
  kind: BoardColumnKind;
  /** The lane this column belongs to. `''` for the unplaced column, which belongs to no lane. */
  lane: string;
  /**
   * The lane's own label, carried on EVERY column including the status columns of an expanded lane.
   * ⭐ Without it the mobile lane tab loses its name the moment its lane is expanded — the label
   * would have to be recovered from a lane column that no longer exists.
   */
  laneLabel: string;
  label: string;
  /**
   * The status codes whose cards land here. A lane column holds many; a status column holds one.
   * ⭐ Cards are placed by MEMBERSHIP OF THIS LIST, never by recomputing the lane at render time —
   * one rule, in one place, so the column a card is drawn in and the column it may be dropped on
   * cannot disagree.
   */
  statuses: string[];
  /** `status_def.colour` for a status column (shot 8). A lane has no colour of its own. */
  colour: string | null;
}

export const UNPLACED_COLUMN_KEY = '__unplaced';

export interface BuildColumnsInput {
  statuses: readonly StatusDef[];
  /** ⚠️ May be empty — `GET /settings/lanes` is a separate row set. See `laneOrderFallback`. */
  lanes: readonly LaneDef[];
  expandedLanes: ReadonlySet<string>;
  /**
   * The status codes that actually occur on the tickets this board is showing.
   * ⭐ This is what keeps an inactive or unknown status on screen: the vocabulary decides the
   * DEFAULT columns, the tickets decide which extra ones are unavoidable.
   */
  occupiedStatuses: ReadonlySet<string>;
}

interface LaneBucket {
  code: string;
  label: string;
  sortOrder: number;
  statuses: StatusDef[];
}

/**
 * When `/settings/lanes` gave us nothing, lane ORDER is still recoverable from the statuses: a lane's
 * position is the lowest `sort_order` of the statuses in it, which is how the seed was written.
 * ⚠️ The lane LABEL is not recoverable, so the code is shown — and `BoardPage` says why. ⛔ It is not
 * title-cased into "Dev", which would look like a label and be one only by luck.
 */
function laneOrderFallback(statuses: readonly StatusDef[]): Map<string, number> {
  const lowest = new Map<string, number>();
  for (const status of statuses) {
    const current = lowest.get(status.lane);
    if (current === undefined || status.sort_order < current) lowest.set(status.lane, status.sort_order);
  }
  return lowest;
}

export function buildColumns(input: BuildColumnsInput): BoardColumn[] {
  const { statuses, lanes, expandedLanes, occupiedStatuses } = input;

  const known = new Map(statuses.map((status) => [status.code, status]));
  const laneDefs = new Map(lanes.map((lane) => [lane.code, lane]));
  const fallbackOrder = laneOrderFallback(statuses);

  const buckets = new Map<string, LaneBucket>();
  const bucketFor = (code: string): LaneBucket => {
    const existing = buckets.get(code);
    if (existing) return existing;
    const def = laneDefs.get(code);
    const created: LaneBucket = {
      code,
      label: def?.label ?? code,
      sortOrder: def?.sort_order ?? fallbackOrder.get(code) ?? Number.MAX_SAFE_INTEGER,
      statuses: [],
    };
    buckets.set(code, created);
    return created;
  };

  // Every lane the vocabulary declares gets a column even when empty — an empty `Review` column is a
  // fact about the project, and a lane that appears only once something is in it makes the board's
  // shape change under the reader.
  for (const lane of lanes) {
    if (lane.is_active) bucketFor(lane.code);
  }

  for (const status of statuses) {
    // ⭐ `is_active || occupied` — a retired status is hidden until a ticket is still sitting in it.
    if (!status.is_active && !occupiedStatuses.has(status.code)) continue;
    bucketFor(status.lane).statuses.push(status);
  }

  const unplaced = [...occupiedStatuses].filter((code) => {
    const status = known.get(code);
    // Unknown to the vocabulary, or in a lane nothing else knows about.
    return status === undefined || !buckets.has(status.lane);
  });

  const ordered = [...buckets.values()]
    .filter((bucket) => bucket.statuses.length > 0 || laneDefs.get(bucket.code)?.is_active)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code));

  const columns: BoardColumn[] = [];

  for (const bucket of ordered) {
    const inLane = [...bucket.statuses].sort(
      (a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code),
    );

    if (!expandedLanes.has(bucket.code)) {
      columns.push({
        key: `lane:${bucket.code}`,
        kind: 'lane',
        lane: bucket.code,
        laneLabel: bucket.label,
        label: bucket.label,
        statuses: inLane.map((status) => status.code),
        colour: null,
      });
      continue;
    }

    for (const status of inLane) {
      columns.push({
        key: `status:${status.code}`,
        kind: 'status',
        lane: bucket.code,
        laneLabel: bucket.label,
        label: status.label,
        statuses: [status.code],
        colour: status.colour,
      });
    }
  }

  if (unplaced.length > 0) {
    columns.push({
      key: UNPLACED_COLUMN_KEY,
      kind: 'unplaced',
      lane: '',
      laneLabel: 'Not in any lane',
      label: 'Not in any lane',
      statuses: [...unplaced].sort(),
      colour: null,
    });
  }

  return columns;
}

/** status code → the key of the column its cards belong in. Built once per render, used per card. */
export function columnIndex(columns: readonly BoardColumn[]): Map<string, string> {
  const index = new Map<string, string>();
  for (const column of columns) {
    for (const status of column.statuses) index.set(status, column.key);
  }
  return index;
}

/** The distinct lanes on the board, in column order — the mobile lane switcher's tab list. */
export function lanesOf(columns: readonly BoardColumn[]): { code: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const column of columns) {
    if (seen.has(column.lane)) continue;
    seen.set(column.lane, column.laneLabel);
  }
  return [...seen].map(([code, label]) => ({ code, label }));
}

/** The columns belonging to one lane — what the phone shows, one lane at a time. */
export function columnsInLane(columns: readonly BoardColumn[], lane: string): BoardColumn[] {
  return columns.filter((column) => column.lane === lane);
}
